import { Controller, useFormContext } from 'react-hook-form';
import { Props as ReactSelectProps } from 'react-select';
import { FormControl, FormLabel, FormErrorMessage } from '@chakra-ui/react';
import get from 'lodash/get';
import SingleSelect from 'components/Form/Select/SingleSelect';
import MultiSelect from 'components/Form/Select/MultiSelect';

// eslint-disable-next-line @typescript-eslint/no-restricted-types
const isOptionType = (a: unknown): a is OptionType => {
  return (a as OptionType)?.value !== undefined;
};

interface SelectProps extends ReactSelectProps {
  options: OptionType[];
  isMulti?: boolean;
  disabled?: boolean;
  openMenuOnFocus?: boolean;
  autoFocus?: boolean;
  label: string;
  name: string;
  placeholder?: string;
  isRequired?: boolean;
  hidden?: boolean;
  showError?: boolean;
  isLoading?: boolean;
  onChangeCallback?: (kind: string) => void;
}

function Select({
  label,
  name,
  options,
  placeholder,
  isRequired = false,
  isMulti,
  disabled,
  openMenuOnFocus,
  autoFocus = false,
  hidden = false,
  showError,
  isLoading,
  onChangeCallback,
  ...props
}: SelectProps) {
  const {
    formState: { errors },
    control,
  } = useFormContext();
  const errorMessage = get(errors, name)?.message as string;
  const SelectComponent = isMulti ? MultiSelect : SingleSelect;

  return (
    <FormControl hidden={hidden} isInvalid={Boolean(errorMessage)} isRequired={isRequired}>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => {
          const pickedValues = Array.isArray(value)
            ? options.filter((option) => value.includes(option.value))
            : [options.find((option) => option.value === value)];

          return (
            <SelectComponent
              placeholder={placeholder || 'Wybierz'}
              options={options}
              inputId={name}
              value={pickedValues || null}
              // eslint-disable-next-line @typescript-eslint/no-restricted-types
              onChange={(newValue: OptionType | OptionType[] | unknown) => {
                if (Array.isArray(newValue)) {
                  onChange((newValue as OptionType[]).map((option) => option.value));
                } else if (isOptionType(newValue)) {
                  onChange(newValue.value);
                } else {
                  onChange(newValue);
                }

                if (typeof onChangeCallback === 'function' && newValue) {
                  const { value: kind } = newValue as OptionType;
                  onChangeCallback(kind as string);
                }
              }}
              isMulti={isMulti}
              isDisabled={disabled}
              isLoading={isLoading}
              openMenuOnFocus={openMenuOnFocus}
              autoFocus={autoFocus}
              instanceId={name}
              closeMenuOnSelect={!isMulti}
              withError={Boolean(errorMessage)}
              noOptionsMessage={({ inputValue }) => (!inputValue ? null : 'Brak wyników')}
              {...props}
            />
          );
        }}
      />
      {showError && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </FormControl>
  );
}
Select.defaultProps = {
  isMulti: false,
  disabled: false,
  openMenuOnFocus: false,
  autoFocus: false,
  placeholder: undefined,
  isRequired: false,
  hidden: false,
  showError: true,
  isLoading: false,
  onChangeCallback: null,
};

export default Select;
